import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FiCheck, FiTrash2, FiUploadCloud } from 'react-icons/fi';

import {
	IVariantValidationErrors,
	hasValidationErrors,
	validateVariant,
} from '@/components/forms/product/schema/variant.schema.errors';
import { MAX_IMAGE_FILE_SIZE } from '@/components/utils/constants';
import { ICreateProductVariantPayload } from '@/interfaces/api/product/ICreateProductPayload';
import { haggleApiService } from '@/services/haggle.service';

type VariantModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (variant: ICreateProductVariantPayload) => void;
	onUpdate?: (variant: ICreateProductVariantPayload, index: number) => void;
	uploadedImages?: string[];
	currentProductImage?: File | null;
	currentProductImageUrl?: string | null;
	variantImageFiles?: File[];
	editingVariant?: ICreateProductVariantPayload;
	editingIndex?: number;
	isEditing?: boolean;
};

const VariantModal: React.FC<VariantModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	onUpdate,
	uploadedImages = [],
	currentProductImage,
	currentProductImageUrl,
	variantImageFiles = [],
	editingVariant,
	editingIndex,
	isEditing,
}) => {
	const initialVariant = {
		name: '',
		sku: '',
		price: 0,
		inventoryQty: 0,
		handle: '',
		type: '',
		grams: 0,
		requiresShipping: true,
		barcode: '',
		image: '',
		weightUnit: '',
	};

	const [variant, setVariant] =
		useState<ICreateProductVariantPayload>(initialVariant);

	const [imageFile, setImageFile] = useState<File | null>(null);
	const [selectedExistingImage, setSelectedExistingImage] =
		useState<string>('');
	const [errors, setErrors] = useState<IVariantValidationErrors>({});

	// Reset state when modal opens/closes or when editing variant changes
	useEffect(() => {
		if (isOpen) {
			if (isEditing && editingVariant) {
				// Pre-populate with existing variant data
				setVariant({
					...editingVariant,
					// Remove imageFile property if it exists
					imageFile: undefined,
				});

				// Handle existing image selection
				if (editingVariant.image) {
					if (editingVariant.image === 'current-product-image') {
						setSelectedExistingImage('current-product-image');
					} else if (editingVariant.image === 'variant-image') {
						// Try to find the variant image in the files
						const filteredVariantImages = variantImageFiles.filter(
							(imageFile) => {
								if (currentProductImage) {
									return !(
										imageFile.name === currentProductImage.name &&
										imageFile.size === currentProductImage.size &&
										imageFile.lastModified === currentProductImage.lastModified
									);
								}
								return true;
							},
						);

						// If we have variant images and imageFile exists, try to match it
						if (editingVariant.imageFile && filteredVariantImages.length > 0) {
							const matchIndex = filteredVariantImages.findIndex(
								(file) =>
									file.name === editingVariant.imageFile?.name &&
									file.size === editingVariant.imageFile?.size,
							);
							if (matchIndex >= 0) {
								setSelectedExistingImage(`variant-image-${matchIndex}`);
							}
						}
					} else if (
						editingVariant.image !== 'new-upload' &&
						editingVariant.image !== 'variant-image' &&
						editingVariant.image !== 'current-product-image'
					) {
						// It's a URL to an existing uploaded image
						setSelectedExistingImage(editingVariant.image);
					}
				}

				setImageFile(null);
			} else {
				// Reset to initial state for adding
				setVariant(initialVariant);
				setImageFile(null);
				setSelectedExistingImage('');
			}
			setErrors({});
		}
	}, [isOpen, isEditing, editingVariant]);

	// Image upload mutation
	const uploadImageMutation = useMutation({
		mutationFn: (file: File) => haggleApiService.uploadImage(file),
	});

	if (!isOpen) return null;

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Escape') onClose();
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type } = e.target;

		if (type === 'number') {
			// Handle numeric inputs properly
			const numericValue = value === '' ? 0 : Number(value);
			setVariant((prev) => ({
				...prev,
				[name]: numericValue,
			}));
		} else {
			setVariant((prev) => ({
				...prev,
				[name]: value,
			}));
		}
	};

	const handleAdd = async () => {
		const variantToSubmit = { ...variant };

		// If we're editing, preserve the original ID
		if (isEditing && editingVariant?.id) {
			variantToSubmit.id = editingVariant.id;
		}

		// Determine which image to use - don't upload yet, just store the reference
		if (
			selectedExistingImage === 'current-product-image' &&
			currentProductImage
		) {
			// Store reference to current product image file
			variantToSubmit.image = 'current-product-image';
			variantToSubmit.imageFile = currentProductImage;
		} else if (selectedExistingImage?.startsWith('variant-image-')) {
			// Store reference to variant image file
			const index = parseInt(selectedExistingImage.split('-')[2]);
			const filteredVariantImages = getFilteredVariantImages();
			if (filteredVariantImages[index]) {
				variantToSubmit.image = 'variant-image';
				variantToSubmit.imageFile = filteredVariantImages[index];
			}
		} else if (selectedExistingImage) {
			// Store existing image URL
			variantToSubmit.image = selectedExistingImage;
		} else if (imageFile) {
			// Store new image file for later upload
			variantToSubmit.image = 'new-upload';
			variantToSubmit.imageFile = imageFile;
		}

		// Default Grams to 0
		variantToSubmit.grams = 0;

		// Default Weight Unit to 'g'
		variantToSubmit.weightUnit = 'g';

		// Default Requires Shipping to true
		variantToSubmit.requiresShipping = true;

		const validationErrors = validateVariant(variantToSubmit);

		if (hasValidationErrors(validationErrors)) {
			setErrors(validationErrors);
			return;
		}

		if (isEditing && onUpdate && editingIndex !== undefined) {
			onUpdate(variantToSubmit, editingIndex);
		} else {
			onSubmit(variantToSubmit);
		}

		setVariant(initialVariant);
		setImageFile(null);
		setSelectedExistingImage('');
		setErrors({});
		onClose();
	};

	// Add image upload handler
	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > MAX_IMAGE_FILE_SIZE) {
			setErrors((prev) => ({
				...prev,
				image: 'File size must be less than 5MB',
			}));
			e.target.value = '';
			return;
		}
		setImageFile(file);
		setSelectedExistingImage(''); // Clear selected existing image
	};

	const handleRemoveImage = () => {
		setImageFile(null);
		setSelectedExistingImage('');
		setVariant((prev) => ({ ...prev, image: '' }));
	};

	const handleSelectExistingImage = (imageUrl: string) => {
		setSelectedExistingImage(imageUrl);
		setImageFile(null); // Clear uploaded file
	};

	// Helper function to filter out variant images that match the product image
	const getFilteredVariantImages = () => {
		return variantImageFiles.filter((imageFile) => {
			if (currentProductImage) {
				return !(
					imageFile.name === currentProductImage.name &&
					imageFile.size === currentProductImage.size &&
					imageFile.lastModified === currentProductImage.lastModified
				);
			}
			return true;
		});
	};

	const getSelectedImage = () => {
		if (
			selectedExistingImage === 'current-product-image' &&
			currentProductImage
		) {
			return URL.createObjectURL(currentProductImage);
		}
		if (selectedExistingImage?.startsWith('variant-image-')) {
			const index = parseInt(selectedExistingImage.split('-')[2]);
			const filteredVariantImages = getFilteredVariantImages();
			if (filteredVariantImages[index]) {
				return URL.createObjectURL(filteredVariantImages[index]);
			}
		}
		if (selectedExistingImage) return selectedExistingImage;
		if (imageFile) return URL.createObjectURL(imageFile);
		return null;
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
			tabIndex={0}
			aria-modal="true"
			role="dialog"
			aria-label="Add Variant Modal"
			onKeyDown={handleKeyDown}
		>
			<div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto">
				<button
					type="button"
					className="absolute top-4 right-4 text-gray-500 hover:text-red-600 focus:outline-none text-3xl p-2 rounded-full bg-white/80 shadow"
					aria-label="Close modal"
					tabIndex={0}
					onClick={onClose}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') onClose();
					}}
				>
					×
				</button>
				<h3 className="font-bold text-lg mb-4 text-center">
					{isEditing ? 'Edit Variant' : 'Add Variant'}
				</h3>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label htmlFor="type" className="block text-sm font-medium mb-1">
							Type
						</label>
						<input
							id="type"
							name="type"
							type="text"
							value={variant.type}
							placeholder="e.g., Color, Size, Material, Color - Size"
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="Type"
							tabIndex={0}
						/>
						{errors.type && (
							<div className="text-red-500 text-xs mt-1">{errors.type}</div>
						)}
					</div>
					<div>
						<label htmlFor="name" className="block text-sm font-medium mb-1">
							Name
						</label>
						<input
							id="name"
							name="name"
							type="text"
							value={variant.name}
							placeholder="e.g., Red, Large, Cotton, Red - Large"
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="Name"
							tabIndex={0}
						/>
						{errors.name && (
							<div className="text-red-500 text-xs mt-1">{errors.name}</div>
						)}
					</div>
					<div>
						<label htmlFor="sku" className="block text-sm font-medium mb-1">
							SKU
						</label>
						<input
							id="sku"
							name="sku"
							type="text"
							value={variant.sku}
							placeholder="e.g., Shirt-Red-Large"
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="SKU"
							tabIndex={0}
						/>
						{errors.sku && (
							<div className="text-red-500 text-xs mt-1">{errors.sku}</div>
						)}
					</div>
					<div>
						<label
							htmlFor="inventoryQty"
							className="block text-sm font-medium mb-1"
						>
							Quantity
						</label>
						<input
							id="inventoryQty"
							name="inventoryQty"
							type="number"
							value={variant.inventoryQty || ''}
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="Quantity"
							tabIndex={0}
							min={0}
							placeholder="0"
						/>
						{errors.inventoryQty && (
							<div className="text-red-500 text-xs mt-1">
								{errors.inventoryQty}
							</div>
						)}
					</div>
					<div>
						<label htmlFor="price" className="block text-sm font-medium mb-1">
							Price ($)
						</label>
						<input
							id="price"
							name="price"
							type="number"
							value={variant.price || ''}
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="Price"
							tabIndex={0}
							min={0}
							step="0.01"
							placeholder="0.00"
						/>
						{errors.price && (
							<div className="text-red-500 text-xs mt-1">{errors.price}</div>
						)}
					</div>
					<div>
						<label htmlFor="barcode" className="block text-sm font-medium mb-1">
							Barcode
						</label>
						<input
							id="barcode"
							name="barcode"
							type="text"
							value={variant.barcode}
							onChange={handleChange}
							className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
							aria-label="Barcode"
							tabIndex={0}
						/>
					</div>

					{/* Image Section - Full Width */}
					<div className="md:col-span-2">
						<label className="block text-sm font-medium mb-1">Image</label>
						{uploadImageMutation.error && (
							<div className="text-red-500 text-xs mb-2">
								Error uploading image:{' '}
								{uploadImageMutation.error.message || 'Unknown error'}
							</div>
						)}

						{/* Current Selection */}
						{getSelectedImage() && (
							<div className="mb-4">
								<p className="text-sm font-medium mb-2">Selected Image:</p>
								<div className="relative w-40 h-40 inline-block">
									<img
										src={getSelectedImage()!}
										alt="Selected variant"
										className="w-40 h-40 object-contain rounded border"
									/>
									<button
										type="button"
										className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-1 text-black hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-400"
										aria-label="Remove image"
										tabIndex={0}
										onClick={handleRemoveImage}
									>
										<FiTrash2 />
									</button>
								</div>
							</div>
						)}

						{/* Existing Images Gallery */}
						{(uploadedImages.length > 0 ||
							currentProductImage ||
							currentProductImageUrl ||
							getFilteredVariantImages().length > 0) && (
							<div className="mb-4">
								<p className="text-sm font-medium mb-2">
									Select from available images:
								</p>
								<div className="grid grid-cols-6 gap-3">
									{/* Current Product Image */}
									{currentProductImage && (
										<div
											className={`relative cursor-pointer border-2 rounded ${
												selectedExistingImage === 'current-product-image'
													? 'border-blue-500'
													: 'border-gray-200 hover:border-gray-300'
											}`}
											onClick={() =>
												handleSelectExistingImage('current-product-image')
											}
										>
											<img
												src={URL.createObjectURL(currentProductImage)}
												alt="Current product"
												className="w-full h-24 object-contain rounded"
											/>
											{selectedExistingImage === 'current-product-image' && (
												<div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
													<FiCheck size={12} />
												</div>
											)}
											<div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-b">
												Product
											</div>
										</div>
									)}
									{/* Current Product Image URL (for editing) */}
									{currentProductImageUrl && !currentProductImage && (
										<div
											className={`relative cursor-pointer border-2 rounded ${
												selectedExistingImage === currentProductImageUrl
													? 'border-blue-500'
													: 'border-gray-200 hover:border-gray-300'
											}`}
											onClick={() =>
												handleSelectExistingImage(currentProductImageUrl)
											}
										>
											<img
												src={currentProductImageUrl}
												alt="Current product"
												className="w-full h-24 object-contain rounded"
											/>
											{selectedExistingImage === currentProductImageUrl && (
												<div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
													<FiCheck size={12} />
												</div>
											)}
											<div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-b">
												Product
											</div>
										</div>
									)}
									{/* Previously uploaded images */}
									{uploadedImages
										.filter((imageUrl) => imageUrl !== currentProductImageUrl)
										.map((imageUrl, index) => (
											<div
												key={index}
												className={`relative cursor-pointer border-2 rounded ${
													selectedExistingImage === imageUrl
														? 'border-blue-500'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												onClick={() => handleSelectExistingImage(imageUrl)}
											>
												<img
													src={imageUrl}
													alt={`Uploaded ${index + 1}`}
													className="w-full h-24 object-contain rounded"
												/>
												{selectedExistingImage === imageUrl && (
													<div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
														<FiCheck size={12} />
													</div>
												)}
											</div>
										))}
									{/* Variant Images */}
									{getFilteredVariantImages().map((imageFile, index) => {
										const imageKey = `variant-image-${index}`;
										return (
											<div
												key={imageKey}
												className={`relative cursor-pointer border-2 rounded ${
													selectedExistingImage === imageKey
														? 'border-blue-500'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												onClick={() => handleSelectExistingImage(imageKey)}
											>
												<img
													src={URL.createObjectURL(imageFile)}
													alt={`Variant ${index + 1}`}
													className="w-full h-24 object-contain rounded"
												/>
												{selectedExistingImage === imageKey && (
													<div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1">
														<FiCheck size={12} />
													</div>
												)}
												<div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded-b">
													Variant
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}

						{/* Upload New Image */}
						{!getSelectedImage() && (
							<div>
								<label className="flex items-center gap-4 cursor-pointer">
									<div className="flex items-center justify-center w-12 h-12 rounded-full border border-gray-300 text-gray-500">
										<FiUploadCloud size={24} />
									</div>
									<div>
										<p className="text-sm font-medium text-gray-700">
											Upload new image
										</p>
										<p className="text-xs text-gray-400">Select one image</p>
									</div>
									<input
										type="file"
										accept="image/*"
										className="hidden"
										onChange={handleImageChange}
										aria-label="Upload variant image"
									/>
								</label>
								{errors.image && (
									<div className="text-red-500 text-xs mt-1">
										{errors.image}
									</div>
								)}
							</div>
						)}
					</div>

					<div className="flex gap-2 mt-4 md:col-span-2">
						<button
							type="button"
							className="flex-1 bg-purpleBrand-500 hover:bg-purpleBrand-600 text-white font-semibold py-2 rounded focus:outline-none"
							aria-label={isEditing ? 'Update Variant' : 'Add Variant'}
							tabIndex={0}
							onClick={handleAdd}
						>
							{isEditing ? 'Update Variant' : 'Add Variant'}
						</button>
						<button
							type="button"
							className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 rounded focus:outline-none"
							aria-label="Cancel"
							tabIndex={0}
							onClick={onClose}
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VariantModal;
